import {
  BuildInfo,
  BuildInfoDialog,
  FlexContainer,
  ReemaThemeV2,
  RoundedMenu,
} from '@act/storybook';
import {
  Avatar,
  Box,
  Button,
  Divider,
  MenuItem,
  Typography,
} from '@mui/material';
import { useState } from 'react';

export interface AvatarWrapperProps {
  firstLetter: string;
  lastLetter?: string;
  width: string;
  isOnlyFirstLetter?: boolean;
}

export const AvatarWrapper = ({
  firstLetter,
  lastLetter,
  width,
  isOnlyFirstLetter = false,
}: AvatarWrapperProps) => {
  return (
    <Avatar sx={{ width: width, height: width, boxSizing: 'border-box' }}>
      <Typography variant="bodyLarge" component="span">
        {firstLetter}
        {lastLetter && !isOnlyFirstLetter && lastLetter}
      </Typography>
    </Avatar>
  );
};

export interface MenuItemConfig {
  type: 'item' | 'divider';
  label?: string;
  onClick?: () => void;
  icon?: React.ReactNode;
  disabled?: boolean;
  visible?: boolean;
}

export interface AvatarMenuProps {
  user?: {
    firstName?: string;
    lastName?: string;
  };
  menuItems: MenuItemConfig[];
  customMenuItems?: React.ReactNode[];
  isOnlyFirstLetter?: boolean;
  isCompact?: boolean;
}

export const AvatarMenu = ({
  user,
  menuItems,
  customMenuItems = [],
  isOnlyFirstLetter = false,
  isCompact = false,
}: AvatarMenuProps) => {
  const firstLetter = user?.firstName?.toUpperCase()?.[0] || 'U';
  const lastLetter = user?.lastName?.toUpperCase()?.[0] || 'K';

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [buildInfoOpen, setBuildInfoOpen] = useState(false);

  function handleOpen(event: React.MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }

  function handleMenuItemClick(onClick?: () => void) {
    onClick?.();
    handleClose();
  }

  function handleOpenBuildInfo() {
    setBuildInfoOpen(true);
    handleClose();
  }

  function renderMenuItems() {
    return menuItems
      .filter(item => item.visible !== false)
      .map((item, index) => {
        if (item.type === 'divider') {
          return <Divider key={`divider-${index}`} sx={{ marginTop: 2 }} />;
        }

        return (
          <MenuItem
            key={`item-${index}`}
            onClick={() => handleMenuItemClick(item.onClick)}
            disabled={item.disabled}
            sx={{ padding: '8px' }}
          >
            <Typography
              sx={{
                width: '100%',
                display: 'flex',
                gap: 1,
              }}
              variant="bodyLarge"
            >
              {item.icon}
              {item.label}
            </Typography>
          </MenuItem>
        );
      });
  }

  return (
    <>
      <Button
        variant="text"
        sx={{
          textTransform: 'none',
          margin: '0',
          padding: '0',
          boxSizing: 'border-box',
          borderRadius: ReemaThemeV2.LIGHT_MODE.app.borderRadius.lessRounded,
          width: '100%',
          overflow: 'hidden',
          flexShrink: 0,
        }}
        disableRipple
        onClick={handleOpen}
      >
        <FlexContainer
          direction="row"
          gap={2}
          padding={2}
          alignItems="center"
          justifyContent="center"
          width="100%"
          overflow="hidden"
          flexShrink={0}
          hoverBackgroundColor={ReemaThemeV2.LIGHT_MODE.palette.secondary.main}
          hoverColor={ReemaThemeV2.LIGHT_MODE.palette.secondary.contrastText}
          borderRadius="lessRounded"
        >
          <AvatarWrapper
            firstLetter={firstLetter}
            lastLetter={lastLetter}
            isOnlyFirstLetter={isOnlyFirstLetter}
            width="32px"
          />
          {!isCompact && (
            <Typography
              variant="bodyMedium"
              component="span"
              color="inherit"
            >{`${user?.firstName}`}</Typography>
          )}
        </FlexContainer>
      </Button>
      <RoundedMenu
        anchorEl={anchorEl}
        open={open}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        handleClose={handleClose}
      >
        <Box
          sx={{
            width: '250px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <AvatarWrapper firstLetter={firstLetter} width="32px" />
          <Typography
            component="div"
            variant="bodyLarge"
          >{`${user?.firstName} ${user?.lastName}`}</Typography>
        </Box>
        <Divider sx={{ margin: '16px 0' }} />
        {renderMenuItems()}
        {customMenuItems}
        <Divider sx={{ margin: '8px 0' }} />
        {<BuildInfo onClick={handleOpenBuildInfo} />}
      </RoundedMenu>
      <BuildInfoDialog open={buildInfoOpen} setOpen={setBuildInfoOpen} />
    </>
  );
};

AvatarMenu.displayName = 'AvatarMenu';
